import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, CalendarCheck, CalendarCog, GripVertical, Minus, Plus } from 'lucide-react'
import { HelpIcon, ApiError, Button, EMPLOYMENT_TYPE_LABELS, EndpointHint, TIER_LABELS, Skeleton } from '../../core/index.js'
import { useAcceptDraft, useBuildDraft, useDraftSchedule, useSaveShiftPreferences, useShiftPreferences } from '../hooks.js'
import { MonthStepper } from '../components/MonthStepper.js'
import { DraftScheduleReview } from '../components/DraftScheduleView.js'
import { acceptingPeriods, preferencesCloseLine } from '../components/biddingStatus.js'
import type { DayOffPreference, DayOffPriority, PreferenceLimits, ShiftPreferencesView, ShiftTypePreference } from '../types.js'
import { HOURS_PER_SHIFT, PRIORITY_CLASS, PRIORITY_LABELS, SHIFT_TYPE_LABELS, datesOf, defaultShiftTypes, describePeriod, describeTimes, firstWeekdayOf, summarizePreferences } from '../components/preferencesText.js'
import { LoadFailure } from '../../core/index.js'

type Step = 'shifts' | 'hours' | 'pto' | 'daysOff' | 'review'
/** The five steps; the fourth is Days off for a worker who marks them, Days you can work for one who marks availability (part time and PRN by default). */
function stepsFor(picksAvailableDays: boolean): readonly { id: Step; title: string }[] {
  return [
    { id: 'shifts', title: 'Shift preference' },
    { id: 'hours', title: 'Hours target' },
    { id: 'pto', title: 'PTO' },
    { id: 'daysOff', title: picksAvailableDays ? 'Days you can work' : 'Days off' },
    { id: 'review', title: 'Build' },
  ]
}
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const PRIORITY_CYCLE: readonly (DayOffPriority | null)[] = ['high', 'medium', 'low', null]

interface Draft {
  shiftTypes: ShiftTypePreference[]
  hours: { min: number; max: number }
  ptoDates: string[]
  daysOff: DayOffPreference[]
  /** The days the worker can work, for one who marks availability; unused by everyone else. */
  availableDates: string[]
  note: string
}

/** The demo's full-time range, for an API deployed before the limits carried one. */
const FALLBACK_TYPICAL_HOURS = { min: 128, max: 160 } as const

/** The limits as the form reads them, whole even from an API that answers an older shape (`fullTimeHours`, no tier, no `bids`). */
function limitsOf(view: ShiftPreferencesView): PreferenceLimits {
  const raw = (view.limits ?? {}) as Partial<PreferenceLimits> & { fullTimeHours?: { min: number; max: number } }
  const typicalHours = raw.typicalHours ?? raw.fullTimeHours ?? { ...FALLBACK_TYPICAL_HOURS }
  return {
    tier: raw.tier ?? 'gold',
    employmentType: raw.employmentType ?? 'full_time',
    bids: raw.bids ?? true,
    maxHighPriorityDays: raw.maxHighPriorityDays ?? 4,
    hoursMin: raw.hoursMin ?? 0,
    hoursMax: raw.hoursMax ?? 320,
    typicalHours,
    fixedHours: raw.fixedHours ?? typicalHours.min === typicalHours.max,
    contractualFloor: raw.contractualFloor ?? 'flag',
    ptoDaysPerPeriod: raw.ptoDaysPerPeriod ?? null,
    contractedHoursPerWeek: raw.contractedHoursPerWeek ?? null,
    picksAvailableDays: raw.picksAvailableDays ?? false,
  }
}

/** "Full time", "Part time", "PRN" as the hours step names the worker's type. */
function typeWord(limits: PreferenceLimits): string {
  return EMPLOYMENT_TYPE_LABELS[limits.employmentType]
}

function draftFrom(view: ShiftPreferencesView): Draft {
  const saved = view.preferences
  return saved
    ? { shiftTypes: saved.shiftTypes, hours: saved.hours, ptoDates: saved.ptoDates, daysOff: saved.daysOff, availableDates: saved.availableDates ?? [], note: saved.note ?? '' }
    : { shiftTypes: defaultShiftTypes(view.offered), hours: { ...limitsOf(view).typicalHours }, ptoDates: [], daysOff: [], availableDates: [], note: '' }
}

function move<T>(list: readonly T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) {
    return [...list]
  }
  const next = [...list]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item as T)
  return next
}

/**
 * Reordering a short list by dragging its handle, with pointer events so a
 * thumb on a phone works as a mouse does (HTML drag-and-drop does not on
 * touch). The row follows the pointer as a lift of a few pixels; crossing
 * another row's middle swaps them at once, so the list reads right while
 * the finger is still down. The arrows stay for anyone who prefers them.
 */
function useDragReorder(count: number, onMove: (from: number, to: number) => void) {
  const rows = useRef<(HTMLLIElement | null)[]>([])
  const [dragging, setDragging] = useState<number | null>(null)
  const draggingRef = useRef<number | null>(null)
  const [offset, setOffset] = useState(0)
  const startY = useRef(0)

  const setRow = useCallback((index: number) => (element: HTMLLIElement | null) => {
    rows.current[index] = element
  }, [])

  useEffect(() => {
    rows.current.length = count
  }, [count])

  useEffect(() => {
    if (dragging === null) {
      return
    }
    function onPointerMove(event: PointerEvent): void {
      const from = draggingRef.current
      if (from === null) {
        return
      }
      setOffset(event.clientY - startY.current)
      // The row whose middle the pointer has crossed takes the dragged row's place.
      const over = rows.current.findIndex((row, index) => {
        if (!row || index === from) {
          return false
        }
        const box = row.getBoundingClientRect()
        return index < from ? event.clientY < box.top + box.height / 2 : event.clientY > box.top + box.height / 2
      })
      if (over !== -1) {
        onMove(from, over)
        draggingRef.current = over
        setDragging(over)
        startY.current = event.clientY
        setOffset(0)
      }
    }
    function onPointerUp(): void {
      draggingRef.current = null
      setDragging(null)
      setOffset(0)
    }
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
    }
  }, [dragging, onMove])

  function startDrag(index: number, event: React.PointerEvent): void {
    event.preventDefault()
    draggingRef.current = index
    startY.current = event.clientY
    setOffset(0)
    setDragging(index)
  }

  return { setRow, dragging, offset, startDrag }
}

/**
 * The demo's bidding wizard, as autonoME keeps its answers (built 16 October
 * 2026): shift order with never marks, an hours target, PTO days, days off
 * by priority, then a review and the save. The facility's shift types come
 * from the shifts the scheduler published; the schedule itself is built
 * elsewhere, from what is saved here. The worker's own login alone.
 */
export function WorkerPreferences({ workerId, onDone, periods }: { workerId: string; onDone: () => void; /** The months the facility has published shifts for, the first the one to open on; only these are offered. */ periods: readonly string[] }) {
  // The first month with published shifts - next month where it has some - and only those months are offered (the user's call, 16 October 2026).
  const [period, setPeriod] = useState<string | null>(periods[0] ?? null)
  const view = useShiftPreferences(workerId, period)
  const draftView = useDraftSchedule(workerId, period)
  if (view.isPending || draftView.isPending) {
    return <Skeleton lines={5} className="p-8" />
  }
  if (view.isError || draftView.isError) {
    return <LoadFailure className="p-8" message="Your preferences could not be loaded." onRetry={() => void Promise.all([view.refetch(), draftView.refetch()])} retrying={view.isFetching || draftView.isFetching} />
  }
  // A tier the facility assigns rather than asks gets no wizard: the engine builds the month from the rules alone.
  if (!limitsOf(view.data).bids) {
    return <AssignedMonth key={view.data.period} workerId={workerId} view={view.data} periods={periods} onChangePeriod={setPeriod} onDone={onDone} />
  }
  // Keyed on the month alone: a save bumps the preferences' version, and keying on that too remounted the wizard
  // mid-flow - Build my schedule saved, the wizard reset to its first step, and the draft never showed.
  return <Wizard key={view.data.period} workerId={workerId} view={view.data} periods={periods} startOnDraft={draftView.data.draft !== null} onChangePeriod={setPeriod} onDone={onDone} />
}

/**
 * The screen for a tier that does not bid (v208 §8, probationary by default):
 * no preferences to give, one button that has the engine assign the month -
 * weekends first, then weekdays, by the facility's usual shift for each day -
 * and the draft to review and submit like anyone else's.
 */
function AssignedMonth({ workerId, view, periods, onChangePeriod, onDone }: { workerId: string; view: ShiftPreferencesView; periods: readonly string[]; onChangePeriod: (period: string) => void; onDone: () => void }) {
  const build = useBuildDraft(workerId)
  const accept = useAcceptDraft(workerId)
  const draftQuery = useDraftSchedule(workerId, view.period)
  const limits = limitsOf(view)
  const period = view.period
  const draft = draftQuery.data?.draft ?? null
  const failure = build.isError ? (build.error instanceof ApiError ? build.error.message : 'Your schedule could not be built. Try again.') : null
  return (
    <div className="max-w-md mx-auto flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={onDone} className="inline-flex items-center gap-1 text-[11px] font-bold text-marine hover:text-marine-dk">
          <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
          My shifts
        </button>
        <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{draft ? 'Assigned schedule' : 'Your month'}</div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">
          {describePeriod(period)}
          <EndpointHint className="ml-2">POST /workers/{'{workerId}'}/schedule/draft</EndpointHint>
        </div>
        <PeriodChoice view={view} periods={periods} onChange={onChangePeriod} />
      </div>
      {draft ? (
        <DraftScheduleReview draft={draft} isStale={draftQuery.data?.isStale ?? false} pending={accept.isPending || build.isPending} error={accept.error ?? build.error} onAccept={() => accept.mutate(period)} onChange={() => build.mutate(period)} onRebuild={() => build.mutate(period)} asked={view.preferences} />
      ) : (
        <section className="bg-white border border-marine/15 p-4">
          <h2 className="flex items-center gap-1 text-sm font-extrabold text-ink">Your month is assigned<HelpIcon topic="schedule.worker-assigned" /></h2>
          <p className="mt-0.5 text-[11px] text-ink-mid leading-snug">
            As a {TIER_LABELS[limits.tier].toLowerCase()} {typeWord(limits).toLowerCase()} worker your facility assigns your schedule rather than asking for preferences: every weekend first, then weekdays, by the usual shift for each day, up to {limits.typicalHours.max} hours. Build it to see it, then submit it.
          </p>
          {failure && (
            <p role="alert" className="mt-2 text-[11px] text-coral">
              {failure}
            </p>
          )}
          <div className="mt-3 flex justify-end">
            <Button icon={CalendarCog} disabled={build.isPending} onClick={() => build.mutate(period)}>
              {build.isPending ? 'Building…' : 'Build my schedule'}
            </Button>
          </div>
        </section>
      )}
    </div>
  )
}

/** The months to choose between: those taking preferences that the facility has published shifts for; none when there is only the one. */
function PeriodChoice({ view, periods, onChange }: { view: ShiftPreferencesView; periods: readonly string[]; onChange: (period: string) => void }) {
  const choices = acceptingPeriods(view).filter((choice) => periods.includes(choice))
  if (choices.length < 2) {
    return null
  }
  return (
    <MonthStepper value={view.period} choices={choices} onChange={onChange} />
  )
}

function Wizard({ workerId, view, periods, startOnDraft, onChangePeriod, onDone }: { workerId: string; view: ShiftPreferencesView; periods: readonly string[]; startOnDraft: boolean; onChangePeriod: (period: string) => void; onDone: () => void }) {
  // A worker with a draft lands on it - one that is out of date says so and offers Build again; anyone else starts at the first step.
  const [mode, setMode] = useState<'wizard' | 'draft'>(startOnDraft ? 'draft' : 'wizard')
  const [step, setStep] = useState<Step>('shifts')
  const [draft, setDraft] = useState<Draft>(() => draftFrom(view))
  const save = useSaveShiftPreferences(workerId)
  const build = useBuildDraft(workerId)
  const accept = useAcceptDraft(workerId)
  const draftQuery = useDraftSchedule(workerId, view.period)
  const limits = limitsOf(view)
  const picks = limits.picksAvailableDays ?? false
  const STEPS = stepsFor(picks)
  const stepIndex = STEPS.findIndex((entry) => entry.id === step)
  const period = view.period
  const nothingPublished = view.offered.every((entry) => entry.published === 0)
  const highUsed = draft.daysOff.filter((entry) => entry.priority === 'high').length
  const noDaysToWork = picks && draft.availableDates.length === 0
  const ptoBalance = view.pto?.balanceDays ?? null
  // The smaller of the balance HR holds and the days the tier allows a month is what the form counts against.
  const ptoCap = [ptoBalance, limits.ptoDaysPerPeriod].filter((value): value is number => value !== null).reduce<number | null>((smallest, value) => (smallest === null ? value : Math.min(smallest, value)), null)
  const ptoOver = ptoCap !== null && draft.ptoDates.length > ptoCap
  const hoursOutside = draft.hours.max > limits.typicalHours.max || (limits.contractualFloor === 'block' && draft.hours.min < limits.typicalHours.min)
  const failure = save.isError ? (save.error instanceof ApiError ? save.error.message : 'Your preferences could not be saved. Try again.') : build.isError ? (build.error instanceof ApiError ? build.error.message : 'Your schedule could not be built. Try again.') : null

  function update(changes: Partial<Draft>): void {
    setDraft((current) => ({ ...current, ...changes }))
  }

  const moveShiftType = useCallback((from: number, to: number) => {
    setDraft((current) => ({ ...current, shiftTypes: move(current.shiftTypes, from, to) }))
  }, [])
  const drag = useDragReorder(draft.shiftTypes.length, moveShiftType)

  /** "Build my schedule": save the preferences, then have the engine draft the month from them. */
  function buildSchedule(): void {
    const hours = limits.fixedHours ? { min: limits.typicalHours.min, max: limits.typicalHours.max } : draft.hours
    // A worker who marks the days they can work sends those and no days off; anyone else leaves availability out.
    save.mutate(
      { period, shiftTypes: draft.shiftTypes, hours, ptoDates: draft.ptoDates, daysOff: picks ? [] : draft.daysOff, ...(picks ? { availableDates: draft.availableDates } : {}), note: draft.note.trim() || null, version: view.preferences?.version ?? null },
      { onSuccess: () => build.mutate(period, { onSuccess: () => setMode('draft') }) },
    )
  }

  if (mode === 'draft' && draftQuery.data?.draft) {
    return (
      <div className="max-w-md mx-auto flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between gap-3">
          <button type="button" onClick={onDone} className="inline-flex items-center gap-1 text-[11px] font-bold text-marine hover:text-marine-dk">
            <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
            My shifts
          </button>
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">Draft schedule</div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">
            {describePeriod(period)}
            <EndpointHint className="ml-2">POST /workers/{'{workerId}'}/schedule/draft</EndpointHint>
          </div>
          <PeriodChoice view={view} periods={periods} onChange={onChangePeriod} />
        </div>
        <DraftScheduleReview
          draft={draftQuery.data.draft}
          isStale={draftQuery.data.isStale}
          pending={accept.isPending || build.isPending}
          error={accept.error ?? build.error}
          onAccept={() => accept.mutate(period)}
          onRebuild={() => build.mutate(period)}
          asked={view.preferences}
          onChange={() => {
            setMode('wizard')
            setStep('shifts')
          }}
        />
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={() => (stepIndex === 0 ? onDone() : setStep(STEPS[stepIndex - 1]?.id ?? 'shifts'))} className="inline-flex items-center gap-1 text-[11px] font-bold text-marine hover:text-marine-dk">
          <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
          {stepIndex === 0 ? 'My shifts' : 'Back'}
        </button>
        <ol className="flex items-center gap-1" aria-label="Steps">
          {STEPS.map((entry, index) => (
            <li key={entry.id} aria-current={entry.id === step ? 'step' : undefined} className={`h-1.5 rounded-full transition-all ${index === stepIndex ? 'w-5 bg-coral' : index < stepIndex ? 'w-2 bg-marine' : 'w-2 bg-marine/20'}`} />
          ))}
        </ol>
        <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-ink-mid">{STEPS[stepIndex]?.title}<HelpIcon topic="schedule.worker-preferences" /></div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">
          Bidding for {describePeriod(period)}
          <EndpointHint className="ml-2">PUT /workers/{'{workerId}'}/shift-preferences</EndpointHint>
        </div>
        <PeriodChoice view={view} periods={periods} onChange={onChangePeriod} />
      </div>
      {preferencesCloseLine(view) && <p className="text-[11px] font-bold text-coral">{preferencesCloseLine(view)}. After that they can no longer be changed.</p>}
      {nothingPublished && (
        <p className="border border-gold/60 bg-gold/15 p-2.5 text-[11px] text-ink leading-snug">
          Your facility has not published shifts for {describePeriod(period)} yet, so a draft built now would be empty. You can still save your preferences, or switch month above.
        </p>
      )}

      {step === 'shifts' && (
        <section className="bg-white border border-marine/15 p-4">
          <h2 className="text-sm font-extrabold text-ink">What's your shift preference?</h2>
          <p className="mt-0.5 text-[11px] text-ink-mid">Drag a row by its handle, or tap the arrows, to reorder. Mark any shift you'll never do.</p>
          <ul className="mt-3 flex flex-col gap-2">
            {draft.shiftTypes.map((entry, index) => {
              const offered = view.offered.find((candidate) => candidate.shiftType === entry.shiftType)
              const isDragging = drag.dragging === index
              return (
                <li
                  key={entry.shiftType}
                  ref={drag.setRow(index)}
                  style={isDragging ? { transform: `translateY(${drag.offset}px)` } : undefined}
                  className={`flex items-center gap-2 border p-2.5 ${isDragging ? 'relative z-10 shadow-lg border-marine bg-white' : entry.willDo ? 'border-marine/15 bg-white' : 'border-marine/10 bg-canvas'}`}
                >
                  <button
                    type="button"
                    aria-label={`Drag ${SHIFT_TYPE_LABELS[entry.shiftType]} to reorder`}
                    onPointerDown={(event) => drag.startDrag(index, event)}
                    className="touch-none shrink-0 cursor-grab p-1 text-ink-mid/70 hover:text-marine active:cursor-grabbing"
                  >
                    <GripVertical aria-hidden="true" className="h-4 w-4" />
                  </button>
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-black ${entry.willDo ? 'bg-marine text-white' : 'bg-ink-mid/20 text-ink-mid'}`}>{entry.willDo ? draft.shiftTypes.filter((other) => other.willDo).indexOf(entry) + 1 : '–'}</span>
                  <span className="min-w-0 flex-1">
                    <span className={`block text-xs font-extrabold ${entry.willDo ? 'text-ink' : 'text-ink-mid line-through'}`}>{SHIFT_TYPE_LABELS[entry.shiftType]}</span>
                    <span className="block text-[10px] text-ink-mid">
                      {offered ? describeTimes(offered.startTime, offered.endTime) : ''}
                      {offered && offered.published > 0 && <> · {offered.published} published</>}
                    </span>
                    {offered?.publishedTimes && (
                      <span className="block text-[10px] text-ink-mid">Published before the hours changed: {describeTimes(offered.publishedTimes.startTime, offered.publishedTimes.endTime)}</span>
                    )}
                  </span>
                  <span className="flex flex-col">
                    <button type="button" aria-label={`Move ${SHIFT_TYPE_LABELS[entry.shiftType]} up`} disabled={index === 0} onClick={() => update({ shiftTypes: move(draft.shiftTypes, index, index - 1) })} className="p-0.5 text-marine disabled:opacity-30">
                      <ArrowUp aria-hidden="true" className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" aria-label={`Move ${SHIFT_TYPE_LABELS[entry.shiftType]} down`} disabled={index === draft.shiftTypes.length - 1} onClick={() => update({ shiftTypes: move(draft.shiftTypes, index, index + 1) })} className="p-0.5 text-marine disabled:opacity-30">
                      <ArrowDown aria-hidden="true" className="h-3.5 w-3.5" />
                    </button>
                  </span>
                  <button
                    type="button"
                    aria-pressed={!entry.willDo}
                    onClick={() => update({ shiftTypes: draft.shiftTypes.map((other) => (other.shiftType === entry.shiftType ? { ...other, willDo: !other.willDo } : other)) })}
                    className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-bold ${entry.willDo ? 'border-marine/30 text-ink-mid hover:border-coral hover:text-coral' : 'border-coral bg-coral-lt text-coral'}`}
                  >
                    {entry.willDo ? "Won't do" : 'Never'}
                  </button>
                </li>
              )
            })}
          </ul>
          {!draft.shiftTypes.some((entry) => entry.willDo) && <p className="mt-2 text-[11px] text-coral">Mark at least one shift you will do.</p>}
        </section>
      )}

      {step === 'hours' && limits.fixedHours && (
        <section className="bg-white border border-marine/15 p-4">
          <h2 className="text-sm font-extrabold text-ink">Your hours this month</h2>
          <p className="mt-0.5 text-[11px] text-ink-mid leading-snug">
            {TIER_LABELS[limits.tier]} {typeWord(limits).toLowerCase()} works a fixed {limits.typicalHours.max} hours a month ({Math.round(limits.typicalHours.max / HOURS_PER_SHIFT)} shifts). There is no range to set; the schedule is built to it.
          </p>
          <div className="mt-3 flex items-center justify-between border border-marine/15 p-2.5">
            <span className="text-xs font-bold text-ink">Hours</span>
            <span className="text-lg font-black text-ink">{limits.typicalHours.max}</span>
          </div>
        </section>
      )}

      {step === 'hours' && !limits.fixedHours && (
        <section className="bg-white border border-marine/15 p-4">
          <h2 className="text-sm font-extrabold text-ink">How many hours this month?</h2>
          <p className="mt-0.5 text-[11px] text-ink-mid">Set your target range. The schedule is built within it.</p>
          <div className="mt-3 flex flex-col gap-2">
            {(['min', 'max'] as const).map((bound) => (
              <div key={bound} className="flex items-center justify-between gap-3 border border-marine/15 p-2.5">
                <span className="text-xs font-bold text-ink">{bound === 'min' ? 'Minimum' : 'Maximum'}</span>
                <span className="flex items-center gap-2">
                  <button type="button" aria-label={`${bound === 'min' ? 'Minimum' : 'Maximum'} down ${HOURS_PER_SHIFT} hours`} onClick={() => update({ hours: { ...draft.hours, [bound]: Math.max(limits.contractualFloor === 'block' ? limits.typicalHours.min : limits.hoursMin, draft.hours[bound] - HOURS_PER_SHIFT) } })} className="flex h-7 w-7 items-center justify-center rounded-full border border-marine/30 text-marine">
                    <Minus aria-hidden="true" className="h-3.5 w-3.5" />
                  </button>
                  <span className="w-10 text-center text-lg font-black text-ink">{draft.hours[bound]}</span>
                  <button type="button" aria-label={`${bound === 'min' ? 'Minimum' : 'Maximum'} up ${HOURS_PER_SHIFT} hours`} onClick={() => update({ hours: { ...draft.hours, [bound]: Math.min(limits.typicalHours.max, draft.hours[bound] + HOURS_PER_SHIFT) } })} className="flex h-7 w-7 items-center justify-center rounded-full border border-marine/30 text-marine">
                    <Plus aria-hidden="true" className="h-3.5 w-3.5" />
                  </button>
                  <span className="w-14 text-[10px] text-ink-mid">{Math.round(draft.hours[bound] / HOURS_PER_SHIFT)} shifts</span>
                </span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-center text-[10px] text-ink-mid">
            {TIER_LABELS[limits.tier]} {typeWord(limits).toLowerCase()} works {limits.typicalHours.min}–{limits.typicalHours.max} hrs a month at your facility
            {limits.contractedHoursPerWeek !== null && <>, from your contracted {limits.contractedHoursPerWeek} hrs a week</>}.
            {picks ? ' The ceiling is firm; the days you can work decide the rest, and a month short of the floor is flagged for your scheduler, never refused.' : limits.contractualFloor === 'block' ? ' The floor is contractual and the ceiling is firm.' : ' The ceiling is firm; a lower minimum is drafted and flagged.'}
          </p>
          {draft.hours.min > draft.hours.max && <p className="mt-1 text-[11px] text-coral">The minimum is above the maximum.</p>}
          {draft.hours.max > limits.typicalHours.max && <p className="mt-1 text-[11px] text-coral">The maximum is above your tier’s {limits.typicalHours.max} hours.</p>}
          {limits.contractualFloor === 'block' && draft.hours.min < limits.typicalHours.min && <p className="mt-1 text-[11px] text-coral">The minimum is below your contracted floor of {limits.typicalHours.min} hours.</p>}
        </section>
      )}

      {step === 'pto' && (
        <section className="bg-white border border-marine/15 p-4">
          <h2 className="text-sm font-extrabold text-ink">Any PTO this month?</h2>
          <p className="mt-0.5 text-[11px] text-ink-mid leading-snug">
            PTO is a separate track from days off: it draws from your accrued balance, not your scheduling slots. Your facility decides it, by reliability score within staffing minimums. Mark the days you are asking for.
            {limits.ptoDaysPerPeriod !== null && <> {TIER_LABELS[limits.tier]} may ask for up to {limits.ptoDaysPerPeriod} {limits.ptoDaysPerPeriod === 1 ? 'day' : 'days'} a month.</>}
          </p>
          <div className="mt-2 flex flex-col border border-marine/15 text-[11px] divide-y divide-marine/10">
            <div className="flex items-center justify-between px-2.5 py-1.5">
              <span className="text-ink-mid">PTO days requested</span>
              <span className={`font-bold ${ptoOver ? 'text-coral' : 'text-ink'}`}>{draft.ptoDates.length} {draft.ptoDates.length === 1 ? 'day' : 'days'}</span>
            </div>
            <div className="flex items-center justify-between px-2.5 py-1.5">
              <span className="text-ink-mid">PTO balance remaining</span>
              <span className="font-bold text-ink">{ptoBalance === null ? 'Not on record' : `${Math.max(0, ptoBalance - draft.ptoDates.length)} ${Math.max(0, ptoBalance - draft.ptoDates.length) === 1 ? 'day' : 'days'}`}</span>
            </div>
          </div>
          {ptoOver && <p className="mt-1.5 text-[11px] text-coral">That is more than {ptoBalance !== null && ptoCap === ptoBalance ? `your balance of ${ptoBalance}` : `the ${ptoCap} days your tier may ask for a month`}. Take some back before going on.</p>}
          {ptoBalance === null && <p className="mt-1.5 text-[10px] text-ink-mid">Your facility has not recorded a balance for you, so nothing is counted down here.</p>}
          <MonthGrid
            period={period}
            mark={(date) => (draft.ptoDates.includes(date) ? { label: 'PTO', className: 'bg-marine text-white border-marine' } : draft.daysOff.some((entry) => entry.date === date) || (picks && draft.availableDates.includes(date)) ? { label: picks ? 'can' : 'off', className: 'bg-canvas text-ink-mid border-marine/15' } : null)}
            onTap={(date) => {
              // A day already marked off, or marked as one the worker can work, is not a PTO day too: the API lists a date once.
              if (draft.daysOff.some((entry) => entry.date === date) || (picks && draft.availableDates.includes(date))) {
                return
              }
              if (!draft.ptoDates.includes(date) && ptoCap !== null && draft.ptoDates.length >= ptoCap) {
                return
              }
              update({ ptoDates: draft.ptoDates.includes(date) ? draft.ptoDates.filter((other) => other !== date) : [...draft.ptoDates, date].sort() })
            }}
          />
        </section>
      )}

      {step === 'daysOff' && picks && (
        <section className="bg-white border border-marine/15 p-4">
          <h2 className="text-sm font-extrabold text-ink">Which days can you work?</h2>
          <p className="mt-0.5 text-[11px] text-ink-mid leading-snug">
            As {typeWord(limits).toLowerCase()} staff you mark the days you can work instead of days off. Tap a day to mark it, again to clear it. You are scheduled on those days and no others; every other day is left alone. Extra days come through the open board and Clinical Bridge.
          </p>
          <div className="mt-2 flex items-center justify-between border border-marine/15 px-2.5 py-1.5 text-[11px]">
            <span className="text-ink-mid">Days you can work</span>
            <span className={`font-bold ${noDaysToWork ? 'text-coral' : 'text-ink'}`}>{draft.availableDates.length} {draft.availableDates.length === 1 ? 'day' : 'days'} · about {draft.availableDates.length * HOURS_PER_SHIFT} hrs</span>
          </div>
          {noDaysToWork && <p className="mt-1.5 text-[11px] text-coral">Mark at least one day.</p>}
          <MonthGrid
            period={period}
            mark={(date) => (draft.availableDates.includes(date) ? { label: 'Can', className: 'bg-green/15 text-green border-green/50' } : draft.ptoDates.includes(date) ? { label: 'PTO', className: 'bg-canvas text-ink-mid border-marine/15' } : null)}
            onTap={(date) => {
              if (draft.ptoDates.includes(date)) {
                return
              }
              update({ availableDates: draft.availableDates.includes(date) ? draft.availableDates.filter((other) => other !== date) : [...draft.availableDates, date].sort() })
            }}
          />
        </section>
      )}

      {step === 'daysOff' && !picks && (
        <section className="bg-white border border-marine/15 p-4">
          <h2 className="text-sm font-extrabold text-ink">Which days would you like off?</h2>
          <p className="mt-0.5 text-[11px] text-ink-mid leading-snug">
            Tap a day to mark it High, again for Medium, again for Low, again to clear it. Up to {limits.maxHighPriorityDays} High priority days: those are avoided at all costs, and only a seat your unit is short on, with nobody else off that day at Low or Medium, can take one. Medium and Low are honored where they can be{limits.tier === 'bronze' ? ', though your tier’s rule may turn a Medium day into work' : ''}.
          </p>
          <div className="mt-2 flex items-center justify-between border border-marine/15 px-2.5 py-1.5 text-[11px]">
            <span className="text-ink-mid">High priority days used</span>
            <span className={`font-bold ${highUsed >= limits.maxHighPriorityDays ? 'text-coral' : 'text-ink'}`}>
              {highUsed} of {limits.maxHighPriorityDays}
            </span>
          </div>
          <div className="mt-2 flex gap-1.5">
            {(['high', 'medium', 'low'] as const).map((priority) => (
              <span key={priority} className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${PRIORITY_CLASS[priority]}`}>
                {PRIORITY_LABELS[priority]}
              </span>
            ))}
          </div>
          <MonthGrid
            period={period}
            mark={(date) => {
              const marked = draft.daysOff.find((entry) => entry.date === date)
              if (marked) {
                return { label: PRIORITY_LABELS[marked.priority], className: PRIORITY_CLASS[marked.priority] }
              }
              return draft.ptoDates.includes(date) ? { label: 'PTO', className: 'bg-canvas text-ink-mid border-marine/15' } : null
            }}
            onTap={(date) => {
              if (draft.ptoDates.includes(date)) {
                return
              }
              const current = draft.daysOff.find((entry) => entry.date === date)?.priority ?? null
              let next = PRIORITY_CYCLE[(PRIORITY_CYCLE.indexOf(current) + 1) % PRIORITY_CYCLE.length] ?? null
              if (next === 'high' && highUsed >= limits.maxHighPriorityDays) {
                next = 'medium'
              }
              const others = draft.daysOff.filter((entry) => entry.date !== date)
              update({ daysOff: next === null ? others : [...others, { date, priority: next }].sort((a, b) => a.date.localeCompare(b.date)) })
            }}
          />
        </section>
      )}

      {step === 'review' && (
        <section className="bg-white border border-marine/15 p-4">
          <h2 className="text-sm font-extrabold text-ink">Here's what you're asking for</h2>
          <p className="mt-0.5 text-[11px] text-ink-mid">Build my schedule saves this and drafts {describePeriod(period)} from it under your facility’s rules: your highest-ranked shifts first, spread across the month, around your days off. You review the draft next.</p>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
            {summarizePreferences({ ...draft, note: null, availableDates: picks ? draft.availableDates : null }).map((line) => (
              <div key={line.label} className="contents">
                <dt className="font-bold text-ink-mid">{line.label}</dt>
                <dd className="text-ink">{line.value}</dd>
              </div>
            ))}
          </dl>
          <label className="mt-3 block text-[10px] font-bold text-ink">
            Anything else your scheduler should know
            <textarea value={draft.note} maxLength={500} rows={2} onChange={(event) => update({ note: event.target.value })} placeholder="Optional" className="mt-1 w-full border border-marine/30 bg-white px-2 py-1.5 text-[12px] font-normal text-ink leading-snug" />
          </label>
          {failure && (
            <p role="alert" className="mt-2 text-[11px] text-coral">
              {failure}
            </p>
          )}
        </section>
      )}

      <div className="flex justify-end">
        {step === 'review' ? (
          <Button icon={CalendarCheck} disabled={save.isPending || build.isPending || !draft.shiftTypes.some((entry) => entry.willDo) || draft.hours.min > draft.hours.max || hoursOutside || ptoOver || noDaysToWork} onClick={buildSchedule}>
            {save.isPending ? 'Saving…' : build.isPending ? 'Building…' : 'Build my schedule'}
          </Button>
        ) : (
          <Button icon={ArrowRight} disabled={(step === 'shifts' && !draft.shiftTypes.some((entry) => entry.willDo)) || (step === 'hours' && (draft.hours.min > draft.hours.max || hoursOutside)) || (step === 'pto' && ptoOver) || (step === 'daysOff' && noDaysToWork)} onClick={() => setStep(STEPS[stepIndex + 1]?.id ?? 'review')}>
            Next: {STEPS[stepIndex + 1]?.title}
          </Button>
        )}
      </div>
    </div>
  )
}

/** A month as a tappable grid; `mark` says what a date wears, `onTap` what a tap does. */
function MonthGrid({ period, mark, onTap }: { period: string; mark: (date: string) => { label: string; className: string } | null; onTap: (date: string) => void }) {
  const dates = datesOf(period)
  const lead = firstWeekdayOf(period)
  return (
    <div className="mt-3">
      <div className="grid grid-cols-7 gap-1 text-center text-[9px] font-bold uppercase tracking-wide text-ink-mid">
        {WEEKDAYS.map((day) => (
          <div key={day}>{day}</div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {Array.from({ length: lead }, (_, index) => (
          <div key={`lead-${index}`} />
        ))}
        {dates.map((date) => {
          const marked = mark(date)
          return (
            <button key={date} type="button" onClick={() => onTap(date)} aria-pressed={marked !== null} aria-label={`${date}${marked ? `, ${marked.label}` : ''}`} className={`flex h-11 flex-col items-center justify-center rounded border text-[11px] font-bold transition-colors ${marked ? marked.className : 'border-marine/15 bg-white text-ink hover:border-marine/40'}`}>
              <span>{Number(date.slice(-2))}</span>
              {marked && <span className="text-[8px] font-bold uppercase leading-none">{marked.label}</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}
